/**
 * Preview: a pool of media elements, one clock, and a draw loop.
 *
 * Nothing here decodes anything. Each source gets a hidden <video> or <audio>
 * element and the browser does the work in hardware, which is the reason this
 * app is a couple of hundred kilobytes instead of thirty megabytes.
 *
 * Two things make that harder than it sounds:
 *
 *  - Media elements do not share a clock. Several playing at once drift apart,
 *    so we keep our own wall-clock playhead and nudge any element that has
 *    wandered further than DRIFT_LIMIT from where it should be. Correcting
 *    every frame would stutter; correcting never desynchronises a reaction cam
 *    from the video it is reacting to.
 *  - Hardware decoders only handle a few concurrent streams, often one or two
 *    on a phone. So an element is only allowed to play while its clip is under
 *    the playhead; everything else is paused. A fifteen-clip timeline still
 *    only ever has the two or three decoders it actually needs.
 */

import {
  clipsAt,
  projectDuration,
  sourceById,
  sourceTimeAt,
  type Clip,
  type Project,
} from "./timeline.ts";
import type { ResolvedSource } from "./sources.ts";
import type { Drawable } from "./render.ts";

/** How far an element may wander before we snap it back, in seconds. */
const DRIFT_LIMIT = 0.08;
/** Elements are kept this far either side of the playhead ready to go. */
const PRELOAD_WINDOW = 0.75;

export interface AudioChain {
  element: HTMLMediaElement;
  gain: GainNode;
}

/**
 * Owns the elements for one project. Rebuilt when the set of resolved sources
 * changes, not on every edit: the elements are about *files*, and the timeline
 * moving around above them does not change which files are open.
 */
export class MediaPool {
  private readonly video = new Map<string, HTMLVideoElement>();
  private readonly image = new Map<string, HTMLImageElement>();
  private readonly audio = new Map<string, AudioChain>();
  private context: AudioContext | null = null;

  constructor(private sources: Map<string, ResolvedSource>) {}

  /** Whatever draws this source: a video element, or an image for a still. Null when its file is missing. */
  drawableFor(sourceId: string): Drawable | null {
    return this.sources.get(sourceId)?.ref.still ? this.imageFor(sourceId) : this.videoFor(sourceId);
  }

  /**
   * A still's element. Nothing drives it: it has no clock to keep and no
   * decoder to give back, so it is made once and left alone.
   */
  imageFor(sourceId: string): HTMLImageElement | null {
    const resolved = this.sources.get(sourceId);
    if (!resolved || !resolved.ref.still) return null;
    let element = this.image.get(sourceId);
    if (!element) {
      element = new Image();
      element.src = resolved.url;
      this.image.set(sourceId, element);
    }
    return element;
  }

  /** The element to draw for a moving source, or null when its file is missing or it is a still. */
  videoFor(sourceId: string): HTMLVideoElement | null {
    const resolved = this.sources.get(sourceId);
    if (!resolved || !resolved.ref.hasVideo || resolved.ref.still) return null;
    let element = this.video.get(sourceId);
    if (!element) {
      element = document.createElement("video");
      element.src = resolved.url;
      element.preload = "auto";
      element.playsInline = true;
      // The picture comes from here and the sound from the audio chain, so this
      // element must stay silent or every clip with sound plays twice.
      element.muted = true;
      element.crossOrigin = "anonymous";
      this.video.set(sourceId, element);
    }
    return element;
  }

  /**
   * The sound for a source, routed through a gain node.
   *
   * Volume goes through WebAudio rather than element.volume because a mix needs
   * gains that can exceed 1 and, later, filters and noise reduction in the same
   * graph. An AudioContext can only be created after a gesture, so this returns
   * null until one exists.
   */
  audioFor(sourceId: string): AudioChain | null {
    const resolved = this.sources.get(sourceId);
    if (!resolved || !resolved.ref.hasAudio) return null;
    const context = this.context;
    if (!context) return null;

    let chain = this.audio.get(sourceId);
    if (!chain) {
      // A source can drive both picture and sound. Reusing the video element
      // for both would tie them to one clock, which is exactly what we want:
      // the audio clip and video clip of one file cannot drift apart.
      const element = resolved.ref.hasVideo
        ? (this.videoFor(sourceId) as HTMLMediaElement)
        : Object.assign(document.createElement("audio"), { src: resolved.url, preload: "auto" });
      if (resolved.ref.hasVideo) (element as HTMLVideoElement).muted = false;
      const gain = context.createGain();
      context.createMediaElementSource(element).connect(gain);
      gain.connect(context.destination);
      chain = { element, gain };
      this.audio.set(sourceId, chain);
    }
    return chain;
  }

  /** For the diagnostics readout. */
  audioState(): string {
    return this.context ? `${this.context.state} @ ${this.context.sampleRate}Hz` : "no context";
  }

  /** Called from a user gesture; without one the browser refuses to start audio. */
  ensureAudioContext(): AudioContext {
    if (!this.context) this.context = new AudioContext();
    void this.context.resume();
    return this.context;
  }

  release(): void {
    for (const element of this.video.values()) {
      element.pause();
      element.removeAttribute("src");
      element.load();
    }
    this.video.clear();
    this.image.clear();
    this.audio.clear();
    void this.context?.close();
    this.context = null;
  }
}

export interface Transport {
  playing: boolean;
  /** Seconds on the timeline. */
  playhead: number;
  /**
   * True when the playhead is a destination rather than a report: just after a
   * seek, the elements are wrong and must be moved. False during ordinary
   * playback, when the playhead came from the master element itself and
   * correcting it against a wall clock is what causes the stutter below.
   */
  authoritative: boolean;
  /** The clip whose element is currently driving the clock, if any. */
  masterClipId?: string | undefined;
}

/**
 * Which clip's element should be the clock while playing.
 *
 * A wall clock and a decoder do not keep the same time. Whenever the element
 * fell behind — a slow decode, or the tail of a seek — the drift exceeded the
 * correction threshold, so it was seeked again, which cost more time than it
 * had lost. Scrubbing during playback landed straight in that loop and looked
 * like lag that never settled. Letting one element keep time and having the
 * playhead follow it removes the fight: nothing is corrected because nothing
 * is out of step by definition.
 *
 * Video first, since the picture is what a stutter shows in; audio only if
 * there is no video at this moment. A still or a title has no clock at all,
 * so neither can keep time: a stretch with only those on it runs on the wall
 * clock, like a gap.
 */
export function pickMaster(project: Project, t: number): Clip | null {
  const here = clipsAt(project, t).filter(
    (clip) => clip.kind !== "text" && !sourceById(project, clip.sourceId)?.still,
  );
  const video = here.filter((clip) => clip.kind === "video");
  return video[0] ?? here[0] ?? null;
}

/** Where the timeline is, according to an element that is playing. */
export function timeFromElement(clip: Clip, element: HTMLMediaElement): number {
  return clip.start + (element.currentTime - clip.in);
}

/** An element is fit to keep time only while it is actually running. */
export function canKeepTime(element: HTMLMediaElement): boolean {
  return !element.paused && !element.seeking && element.readyState >= 2;
}

/**
 * Bring every element in line with where the playhead says it should be.
 *
 * Returns nothing and throws nothing: a browser refusing to play, or an element
 * still loading, is normal here and the next tick tries again.
 */
export function syncElements(
  project: Project,
  pool: MediaPool,
  transport: Transport,
): void {
  const active = new Set<string>();

  for (const clip of clipsNear(project, transport.playhead)) {
    // Nothing to drive: no element for a title, and an image needs no seeking.
    if (clip.kind === "text") continue;
    const local = sourceTimeAt(clip, transport.playhead);
    const inside = transport.playhead >= clip.start && transport.playhead < clip.start + (clip.out - clip.in);
    active.add(clip.sourceId);

    // The clip keeping time is not corrected against the playhead it produced.
    // Correcting it would be circular, and it is the correction that stutters.
    const isMaster = clip.id === transport.masterClipId;
    const keepsTime = transport.playing && !transport.authoritative && isMaster;

    /**
     * While the clock holds at a target (authoritative), only the master runs:
     * it has to, so its arrival can be seen. Everything else waits, paused at
     * the target. Left to play, an audio element seeks in a few milliseconds,
     * runs on past the held playhead, is dragged back once it is over the
     * drift limit, runs on again: the same fifth of a second looping until the
     * video's slower seek finished and the clock moved. Paused, it starts
     * cleanly once the clock follows.
     */
    const shouldPlay =
      transport.playing && inside && (!transport.authoritative || isMaster || !transport.masterClipId);

    if (clip.kind === "video") {
      const element = pool.videoFor(clip.sourceId);
      if (element) driveElement(element, local, shouldPlay, keepsTime);
    } else {
      const chain = pool.audioFor(clip.sourceId);
      if (chain) {
        chain.gain.gain.value = clip.muted ? 0 : Math.max(0, clip.volume);
        driveElement(chain.element, local, shouldPlay, keepsTime);
      }
    }
  }

  // Anything the playhead has left goes quiet and gives its decoder back.
  for (const clip of project.clips) {
    if (clip.kind === "text" || active.has(clip.sourceId)) continue;
    const element = pool.videoFor(clip.sourceId);
    if (element && !element.paused) element.pause();
    const chain = pool.audioFor(clip.sourceId);
    if (chain && !chain.element.paused) chain.element.pause();
  }
}

/**
 * Close enough while paused. Half a frame at 30fps.
 *
 * This needs a tolerance at all, and that is not obvious. A paused element is
 * driven every animation frame, and a seek snaps currentTime to a frame
 * boundary rather than to the exact value asked for — so comparing for
 * equality meant the element was always "wrong" and was re-seeked sixty times
 * a second. A perpetually seeking element drops below HAVE_CURRENT_DATA, which
 * renderFrame skips, so a paused video went black and stayed black.
 */
const SCRUB_TOLERANCE = 1 / 60;

function driveElement(
  element: HTMLMediaElement,
  target: number,
  shouldPlay: boolean,
  keepsTime = false,
): void {
  if (!Number.isFinite(target) || target < 0) return;

  // A seek takes tens of milliseconds, and this runs every animation frame.
  // Issuing another one while the first is still in flight means the element
  // never arrives anywhere: each correction is measured against a position it
  // is no longer at, so a jump on the scrubber turned into a burst of seeks
  // that fought each other and took a visible moment to settle. Waiting for
  // the one in progress costs a frame and resolves in one hop.
  if (!keepsTime && !element.seeking) {
    const drift = Math.abs(element.currentTime - target);
    // Paused, the tolerance is tight so scrubbing tracks the playhead closely;
    // playing, it is loose so a small lag is left to resolve itself rather than
    // corrected into a stutter.
    if (drift > (shouldPlay ? DRIFT_LIMIT : SCRUB_TOLERANCE)) {
      try {
        element.currentTime = target;
      } catch {
        /* not seekable yet */
      }
    }
  }

  if (shouldPlay && element.paused) void element.play().catch(() => undefined);
  if (!shouldPlay && !element.paused) element.pause();
}

/** Clips at the playhead, plus those about to arrive, so decoders warm up early. */
function clipsNear(project: Project, t: number): Clip[] {
  const soon = clipsAt(project, t + PRELOAD_WINDOW);
  const now = clipsAt(project, t);
  const seen = new Set(now.map((clip) => clip.id));
  return [...now, ...soon.filter((clip) => !seen.has(clip.id))];
}

/**
 * A wall-clock playhead.
 *
 * The clock is ours rather than any element's currentTime, because the timeline
 * has gaps: at a point where no clip is playing there is no element to ask, and
 * the playhead still has to move.
 */
export class Clock {
  private origin = 0;
  private startedAt = 0;
  private running = false;
  private authoritative = true;

  get playing(): boolean {
    return this.running;
  }

  /**
   * True while the clock is telling the elements where to go, rather than
   * reporting where they are. Set by an explicit seek and cleared once the
   * elements have arrived.
   */
  get leads(): boolean {
    return this.authoritative;
  }

  /**
   * While the clock leads, it holds still.
   *
   * Leading means the elements are being sent somewhere and have not arrived.
   * If the clock kept running meanwhile, the destination would move under
   * them: on a large file a seek takes longer than any reasonable handover
   * window, so by the time the element landed the clock had moved on, the
   * drift was over the limit, and it was seeked again — a chase the element
   * could never win. Measured on a 2732x1440 clip: two seeks every half
   * second, the element permanently in the seeking state, and the picture a
   * beat and a half behind the playhead. Holding the destination still means
   * one seek lands and the element takes over from there.
   */
  now(): number {
    if (!this.running || this.authoritative) return this.origin;
    return this.origin + (performance.now() - this.startedAt) / 1000;
  }

  /** How long the clock has been leading, so a hold can be given up on. */
  leadingFor(): number {
    return this.authoritative && this.running ? performance.now() - this.startedAt : 0;
  }

  seek(t: number): void {
    this.origin = Math.max(0, t);
    this.startedAt = performance.now();
    this.authoritative = true;
  }

  /** Adopt the time an element reports, without becoming its destination. */
  follow(t: number): void {
    this.origin = Math.max(0, t);
    this.startedAt = performance.now();
    this.authoritative = false;
  }

  start(): void {
    if (this.running) return;
    this.origin = this.now();
    this.startedAt = performance.now();
    this.running = true;
    // Pressing play is a destination too: the elements are wherever the last
    // scrub left them and have to be brought to the playhead once.
    this.authoritative = true;
  }

  stop(): void {
    if (!this.running) return;
    this.origin = this.now();
    this.running = false;
    this.authoritative = true;
  }

  /** True when the playhead has run off the end of the edit. */
  past(project: Project): boolean {
    return this.now() >= projectDuration(project);
  }
}
